// B2B / Akce (schválený mockup 7): seznam (tabulka, na telefonu řádky) a kalendář měsíce.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Plus, RefreshCw, List, CalendarDays, Search } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Segment, PruhFiltru, RozbalovaciVyber, ListovaniMesice, ErrorNote, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { Pil, RadekKontaktu, KartaObal } from "../../components/sales/salesUi.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { ton as tonPodleKlice } from "../../lib/tony.js";
import { b2bGet } from "../../components/b2b/b2bApi.js";
import { StavAkcePil, STAVY_AKCE, volbyStavuAkce, kc, terminText, marzePil, dnesIsoPraha } from "../../components/b2b/b2bUi.jsx";
import { AkceDialog } from "../../components/b2b/AkceDialogy.jsx";

const REZIMY = [
  { key: "seznam", label: <span className="inline-flex items-center gap-1.5"><List className="h-4 w-4" />Seznam</span> },
  { key: "kalendar", label: <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />Kalendář</span> },
];
const DNY = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const MESICE = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const jmenoFirmy = (n) => String(n || "").replace(/,?\s*(s\.\s?r\.\s?o\.|a\.\s?s\.)$/i, "");
const pad = (n) => String(n).padStart(2, "0");

function Tabulka({ akce, prah, navigate }) {
  return (
    <KartaObal>
      <div className="p-3 sm:p-4"><div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-3 py-2">Akce</th><th className="px-3 py-2">Termín</th><th className="px-3 py-2">Stav</th><th className="px-3 py-2">Lidí</th><th className="px-3 py-2">Cena bez DPH</th><th className="px-3 py-2">Marže</th>
          </tr></thead>
          <tbody>
            {akce.map((a) => {
              const t = terminText(a);
              return (
                <tr key={a.id} className="cursor-pointer border-t border-slate-100 align-top" onClick={() => navigate(`/b2b/akce/${a.id}`)}>
                  <td className="px-3 py-2.5"><div className="font-bold text-brand-deep">{a.nazev}</div><div className="text-xs text-slate-500">{[a.firma_nazev, a.produkt_nazev].filter(Boolean).join(", ")}</div></td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-800">{t.datum}{t.cas && <div className="text-xs text-slate-500">{t.cas}</div>}</td>
                  <td className="px-3 py-2.5"><StavAkcePil stav={a.stav} mala /></td>
                  <td className="px-3 py-2.5 tabular-nums text-slate-700">{a.pocet_lidi ?? ""}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-semibold tabular-nums text-slate-900">{kc(a.cena_bez_dph)}</td>
                  <td className="px-3 py-2.5">{marzePil(a, prah)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div></div>
    </KartaObal>
  );
}
function Radky({ akce, navigate }) {
  return (
    <KartaObal>
      {akce.map((a, i) => {
        const t = terminText(a);
        return (
          <RadekKontaktu key={a.id} prvni={i === 0} jmeno={a.nazev} onOtevrit={() => navigate(`/b2b/akce/${a.id}`)}
            pilulky={<><StavAkcePil stav={a.stav} /><Pil ton="neutralni" ikona={CalendarCheck} casti={[t.datum, t.cas].filter(Boolean)} />{a.cena_bez_dph != null && <Pil ton="neutralni" casti={kc(a.cena_bez_dph)} />}</>}
            poznamka={[a.firma_nazev, a.produkt_nazev, a.pocet_lidi ? `${a.pocet_lidi} lidí` : null].filter(Boolean).join(", ")} />
        );
      })}
    </KartaObal>
  );
}

function KalendarPc({ akce, rok, mesic, navigate }) {
  const prvni = new Date(Date.UTC(rok, mesic, 1));
  const predchozi = (prvni.getUTCDay() + 6) % 7;
  const dnuVMesici = new Date(Date.UTC(rok, mesic + 1, 0)).getUTCDate();
  const bunky = [...Array(predchozi).fill(null), ...Array.from({ length: dnuVMesici }, (_, i) => i + 1)];
  while (bunky.length % 7) bunky.push(null);
  const dnes = dnesIsoPraha();
  return (
    <KartaObal>
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50 text-center text-xs font-bold uppercase tracking-wider text-slate-500">
        {DNY.map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {bunky.map((d, i) => {
          const iso = d ? `${rok}-${pad(mesic + 1)}-${pad(d)}` : null;
          const a = iso ? akce.filter((x) => x.datum === iso) : [];
          return (
            <div key={i} className={`min-h-[104px] border-b border-r border-slate-100 p-1.5 ${d ? "" : "bg-slate-50/60"} ${i % 7 > 4 ? "bg-slate-50/40" : ""}`}>
              {d && <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold tabular-nums ${iso === dnes ? "bg-brand text-white" : "text-slate-600"}`}>{d}</div>}
              <div className="space-y-1">
                {a.map((x) => (
                  <button key={x.id} type="button" onClick={() => navigate(`/b2b/akce/${x.id}`)} title={x.nazev}
                    className={`block w-full truncate rounded-lg px-1.5 py-1 text-left text-[11px] font-bold leading-tight ${tonPodleKlice(STAVY_AKCE[x.stav]?.ton || "neutralni").pilulka}`}>
                    {x.cas_od ? `${x.cas_od} ` : ""}{jmenoFirmy(x.firma_nazev)}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </KartaObal>
  );
}
function KalendarTelefon({ akce, navigate }) {
  if (!akce.length) return <KartaObal><EmptyState icon={CalendarDays} title="Tenhle měsíc nic neběží" hint="Akce s datem se objeví tady." /></KartaObal>;
  return (
    <KartaObal>
      {akce.map((a, i) => (
        <div key={a.id} className={`flex items-stretch gap-3 bg-white px-4 py-3 ${i ? "border-t border-slate-100" : ""}`}>
          <div className="flex w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-brand-50 py-1.5 text-brand-deep">
            <span className="text-lg font-bold leading-none tabular-nums">{Number(a.datum.slice(8))}</span>
            <span className="text-[11px] font-bold uppercase">{DNY[(new Date(`${a.datum}T12:00:00Z`).getUTCDay() + 6) % 7]}</span>
          </div>
          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => navigate(`/b2b/akce/${a.id}`)}>
            <div className="text-base font-bold leading-snug text-slate-900">{a.nazev}</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5"><StavAkcePil stav={a.stav} /><Pil ton="neutralni" casti={terminText(a).cas || "čas neurčen"} /></div>
            <div className="mt-1 text-[13px] text-slate-600">{a.firma_nazev}</div>
          </button>
        </div>
      ))}
    </KartaObal>
  );
}

export default function Akce() {
  const uzka = useUzkaObrazovka();
  const navigate = useNavigate();
  const dnes = dnesIsoPraha();
  const [rezim, setRezim] = useState("seznam");
  const [stav, setStav] = useState("");
  const [produkt, setProdukt] = useState("");
  const [q, setQ] = useState("");
  const [rok, setRok] = useState(Number(dnes.slice(0, 4)));
  const [mesic, setMesic] = useState(Number(dnes.slice(5, 7)) - 1);
  const [d, setD] = useState(null);
  const [produkty, setProdukty] = useState([]);
  const [chyba, setChyba] = useState("");
  const [nova, setNova] = useState(false);

  const nacti = useCallback(async () => {
    try {
      const params = { q, stav, produkt };
      if (rezim === "kalendar") { params.od = `${rok}-${pad(mesic + 1)}-01`; params.do = `${rok}-${pad(mesic + 1)}-${pad(new Date(Date.UTC(rok, mesic + 1, 0)).getUTCDate())}`; }
      setD(await b2bGet("akce_seznam", params)); setChyba("");
    } catch (e) { setChyba(e.message); }
  }, [q, stav, produkt, rezim, rok, mesic]);
  useEffect(() => { const t = setTimeout(nacti, q ? 250 : 0); return () => clearTimeout(t); }, [nacti, q]);
  useEffect(() => { b2bGet("produkty").then((r) => setProdukty(r.produkty || [])).catch(() => {}); }, []);

  const produktVolby = useMemo(() => [{ key: "", label: "Všechny" }, ...produkty.map((p) => ({ key: String(p.id), label: p.nazev }))], [produkty]);
  const posun = (n) => { const dt = new Date(Date.UTC(rok, mesic + n, 1)); setRok(dt.getUTCFullYear()); setMesic(dt.getUTCMonth()); };
  const akce = d?.akce || [];
  const razene = rezim === "seznam" ? [...akce].sort((a, b) => (b.datum || "9999").localeCompare(a.datum || "9999") || b.id - a.id) : akce.filter((a) => a.datum).sort((a, b) => a.datum.localeCompare(b.datum));
  const prah = d?.marze_prah ?? 30;

  return (
    <Stranka sirka={rezim === "kalendar" && !uzka ? "plna" : "bezna"}>
      <GradientHeader title="Akce" icon={CalendarCheck} akce={[{ klic: "nova", ikona: Plus, popis: "Nová akce", onClick: () => setNova(true) }, { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: nacti }]} />
      <PruhFiltru>
        <div className="grid gap-3">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,230px)]">
            <div className="relative min-w-0">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="h-4 w-4" /></span>
              <input className="field !min-h-[56px] !rounded-2xl !pl-9" placeholder="Hledat akci nebo firmu" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Hledat akci" />
            </div>
            <Segment items={REZIMY} value={rezim} onChange={setRezim} className="self-stretch" aria-label="Seznam nebo kalendář" />
          </div>
          <div className={"grid gap-3 " + (uzka ? "" : "lg:grid-cols-2")}>
            <RozbalovaciVyber stitek="Stav" items={volbyStavuAkce()} value={stav} onChange={setStav} aria-label="Stav" />
            <RozbalovaciVyber stitek="Produkt" items={produktVolby} value={produkt} onChange={setProdukt} aria-label="Produkt" />
          </div>
        </div>
      </PruhFiltru>
      {rezim === "kalendar" && <ListovaniMesice popis={`${MESICE[mesic]} ${rok}`} onZpet={() => posun(-1)} onVpred={() => posun(1)} naDnesek={() => { setRok(Number(dnes.slice(0, 4))); setMesic(Number(dnes.slice(5, 7)) - 1); }} />}
      {chyba && <ErrorNote onRetry={nacti}>{chyba}</ErrorNote>}
      {!chyba && !d && <SkeletonRows rows={5} />}
      {!chyba && d && rezim === "seznam" && (razene.length === 0
        ? <KartaObal><EmptyState icon={CalendarCheck} title="Zatím tu není žádná akce" hint="Akci založíš tlačítkem Nová akce nebo z detailu firmy."
            action={<button type="button" className="btn-primary" onClick={() => setNova(true)}><Plus className="h-4 w-4" />Nová akce</button>} /></KartaObal>
        : (uzka ? <Radky akce={razene} navigate={navigate} /> : <Tabulka akce={razene} prah={prah} navigate={navigate} />))}
      {!chyba && d && rezim === "kalendar" && (uzka ? <KalendarTelefon akce={razene} navigate={navigate} /> : <KalendarPc akce={razene} rok={rok} mesic={mesic} navigate={navigate} />)}
      <AkceDialog open={nova} onClose={() => setNova(false)} onHotovo={(a) => { setNova(false); navigate(`/b2b/akce/${a.id}`); }} />
    </Stranka>
  );
}
